<template>
  <form @submit.prevent="submit">
    <v-autocomplete
      ref="searchBox"
      v-model="select"
      class="bm-search"
      :loading="loading"
      :items="items"
      :search-input.sync="search"
      flat
      hide-details
      hide-selected
      :label="$m.search"
      :solo-inverted="!theme.isDark()"
      :solo="theme.isDark()"
      no-filter
      clearable
      :prepend-inner-icon="bmIconName('Search')"
    >
      <template #no-data>
        <v-list-item>
          <v-list-item-title>
            Search for outlets, contacts, orders, and more
          </v-list-item-title>
        </v-list-item>
      </template>
      <template #item="{ item }">
        <v-list-item :to="item.navigationRoute" @click="clearSelection">
          <v-list-item-content>
            <v-list-item-title>
              {{ item.description }}
              <bm-icon v-if="item.isHidden" icon="VisibilityOff" class="pl-2" />
            </v-list-item-title>
            <v-list-item-subtitle v-text="item.typeName">
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-autocomplete>
  </form>
</template>

<script setup lang="ts">
import type { IResolvedSearchResult } from "@/app/services/extensions/searchServiceExtended";
import { searchService } from "@/app/services/extensions/searchServiceExtended";
import debounce from "lodash-es/debounce";
import { menuSearch } from "@/app/ui/navigation/menuItems";
import { theme } from "@/app/theme";
import { mainRoutes } from "@/app/ui/navigation/mainMenu";
import { bmIconName } from "@/components/controls/bmIconMap";
import { makeRef } from "@/components/composables/componentTools";
import { watch, ref } from "vue";
import { useRouter } from "@/router/router";

const { router } = useRouter();

const loading = makeRef(false);
const items = makeRef<IResolvedSearchResult[]>([]);
const search = makeRef<string | null>(null);
const select = makeRef(null);
const searchBox = ref<{ blur: () => void } | null>(null);

const doSearch = debounce(async (val: string) => {
  try {
    if (!val) {
      items.value = [];
      return;
    }

    loading.value = true;
    items.value = [
      ...(await searchService.typedSearch(val)),
      ...menuSearch(val),
    ];
  } finally {
    loading.value = false;
  }
}, 300);

watch(search, async (val) => {
  await doSearch(val ?? "");
});

function clearSelection() {
  select.value = null;
  searchBox.value?.blur();
}

function submit() {
  router.push(mainRoutes.search.location(search.value ?? ""));
  searchBox.value?.blur();
}
</script>

<style scoped lang="sass">
@import "src/sass/typography"

.bm-search :deep(label)
  @include bm-body2
  color: white

// Hide the little dropdown arrow on the right
.bm-search :deep(.v-input__icon--append)
  display: none

// stop the form element changing how anything displays
form
  display: contents
</style>
